import React, { useState } from 'react';
import { X, Plus, CheckSquare, Clock, AlertCircle } from 'lucide-react';
import { ActionItem, StickyNote } from '../types';
import { useSocket } from '../context/SocketContext';

interface ActionItemsPanelProps {
  boardId: string;
  actionItems: ActionItem[];
  notes: StickyNote[];
  onClose: () => void;
}

const ActionItemsPanel: React.FC<ActionItemsPanelProps> = ({
  boardId,
  actionItems,
  notes,
  onClose,
}) => {
  const { socket } = useSocket();
  const [showAddAction, setShowAddAction] = useState(false);
  const [newAction, setNewAction] = useState({
    title: '',
    description: '',
    priority: 'medium' as ActionItem['priority'],
    assignee: '',
    dueDate: '',
  });

  const handleAddAction = () => {
    if (!socket || !newAction.title.trim()) return;

    const action: Omit<ActionItem, 'id'> = {
      title: newAction.title.trim(),
      description: newAction.description.trim() || undefined,
      priority: newAction.priority,
      status: 'pending',
      assignee: newAction.assignee.trim() || undefined,
      dueDate: newAction.dueDate ? new Date(newAction.dueDate) : undefined,
    };

    socket.emit('action-added', { boardId, action });

    // Reset form
    setNewAction({
      title: '',
      description: '',
      priority: 'medium',
      assignee: '',
      dueDate: '',
    });
    setShowAddAction(false);
  };

  const handleUpdateAction = (action: ActionItem) => {
    if (!socket) return;
    socket.emit('action-updated', { boardId, action });
  };

  const handleDeleteAction = (actionId: string) => {
    if (!socket) return;
    if (window.confirm('Are you sure you want to delete this action item?')) {
      socket.emit('action-deleted', { boardId, actionId });
    }
  };

  const getPriorityColor = (priority: ActionItem['priority']) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
    }
  };

  const getStatusColor = (status: ActionItem['status']) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Background overlay */}
        <div 
          className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Panel */}
        <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl">
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="bg-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-white flex items-center">
                  <CheckSquare className="h-5 w-5 mr-2" />
                  Action Items ({actionItems.length})
                </h2>
                <button
                  onClick={onClose}
                  className="text-indigo-200 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              {/* Add new action */}
              <div className="mb-6">
                <button
                  onClick={() => setShowAddAction(!showAddAction)}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Action Item</span>
                </button>
                
                {showAddAction && (
                  <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      placeholder="Action title *"
                      value={newAction.title}
                      onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    
                    <textarea
                      placeholder="Description (optional)"
                      value={newAction.description}
                      onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    
                    <div className="grid grid-cols-3 gap-4">
                      <select
                        value={newAction.priority}
                        onChange={(e) => setNewAction({ ...newAction, priority: e.target.value as ActionItem['priority'] })}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                      </select>
                      
                      <input
                        type="text"
                        placeholder="Assignee"
                        value={newAction.assignee}
                        onChange={(e) => setNewAction({ ...newAction, assignee: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      
                      <input
                        type="date"
                        value={newAction.dueDate}
                        onChange={(e) => setNewAction({ ...newAction, dueDate: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowAddAction(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddAction}
                        disabled={!newAction.title.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
                      >
                        Add Action
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action items list */}
              <div className="space-y-4">
                {actionItems.map((action) => (
                  <div key={action.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{action.title}</h3>
                      <button
                        onClick={() => handleDeleteAction(action.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {action.description && (
                      <p className="text-sm text-gray-600 mb-3">{action.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(action.priority)}`}>
                        {action.priority.toUpperCase()}
                      </span>
                      
                      <select
                        value={action.status}
                        onChange={(e) => handleUpdateAction({
                          ...action,
                          status: e.target.value as ActionItem['status']
                        })}
                        className={`px-2 py-1 text-xs font-medium rounded-full border-none focus:outline-none cursor-pointer ${getStatusColor(action.status)}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      
                      {action.assignee && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full text-blue-600 bg-blue-100">
                          {action.assignee}
                        </span>
                      )}
                    </div>
                    
                    {action.dueDate && (
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        Due: {new Date(action.dueDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
                
                {actionItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No action items yet</p>
                    <p className="text-sm">Create action items to track next steps from your retrospective</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionItemsPanel;